import { useState, useEffect, useMemo } from 'react';
import { axiosInstance } from '@/axiosConfig';
import { toast } from 'sonner';

// --- Constantes Estáticas ---
export const CATEGORIA_RECEBIMENTO = ["Venda Avulsa", "Reembolso", "Fatura", "Serviço", "Aéreo", "Hospedagem", "Pacote", "Taxa", "Outro"];
export const CATEGORIA_PAGAMENTO = ["Fatura", "Imposto", "Salário", "Pró-labore", "Marketing", "Administrativo", "Pessoal", "Comissão", "Operacional", "Outras"];

// Helper para pegar o primeiro e último dia do mês
const getPrimeiroUltimoDiaDoMes = () => {
  const today = new Date(); 
  const year = today.getFullYear();
  const month = today.getMonth();
  return { from: new Date(year, month, 1), to: new Date(year, month + 1, 0) };
};

// *** MUDANÇA: Adicionado 'export' na linha abaixo ***
export const staticDateRange = getPrimeiroUltimoDiaDoMes();

export const initialCaixaFilters = {
  date: staticDateRange,
  termo_busca: '',
  conta_id: 'all',
  categoria: 'all',
};

// --- Funções de Carregamento de Dados ---

/**
 * Constrói os parâmetros de data para a API.
 * Converte objetos Date para strings ISO 8601 UTC.
 */
export const getParamsForAPI = (filters) => {
  const params = {};
  if (filters.date?.from) {
      const localDate = new Date(filters.date.from);
      params.data_inicio = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 0, 0, 0, 0)).toISOString();
  }
  if (filters.date?.to) {
      const localDate = new Date(filters.date.to);
      params.data_fim = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 23, 59, 59, 999)).toISOString();
  }
  return params;
};

/**
 * Hook customizado para carregar todos os dados de dropdown
 * (Contas, Clientes, Fornecedores, Vendedores)
 * e já processá-los para os Comboboxes.
 */
export const useFinanceiroDropdownData = () => {
    const [contasList, setContasList] = useState([]);
    const [clientesList, setClientesList] = useState([]);
    const [fornecedoresList, setFornecedoresList] = useState([]);
    const [vendedoresList, setVendedoresList] = useState([]); 
    const [loadingDropdowns, setLoadingDropdowns] = useState(true);

    useEffect(() => {
        const loadDropdownData = async () => {
            setLoadingDropdowns(true);
            try {
                const [clientesRes, contasRes, fornecedoresRes, vendedoresRes] = await Promise.all([
                    axiosInstance.get('/clientes'),
                    axiosInstance.get('/contas'),
                    axiosInstance.get('/fornecedores'),
                    axiosInstance.get('/vendedores')
                ]);
                setClientesList(clientesRes.data);
                setContasList(contasRes.data);
                setFornecedoresList(fornecedoresRes.data);
                setVendedoresList(vendedoresRes.data);
            } catch {
                toast.error("Erro ao carregar dados de cadastro.");
            } finally {
                setLoadingDropdowns(false);
            }
        };
        loadDropdownData();
    }, []);

    // Gera lista combinada para "Pagar a" (Vendedores + Fornecedores)
    const beneficiariosPagar = useMemo(() => {
      const fornecedores = fornecedoresList.map(f => ({ id: `f-${f.id}`, nome: `${f.nome} (Fornecedor)` }));
      const vendedores = vendedoresList.map(v => ({ id: `v-${v.id}`, nome: `${v.nome} (Vendedor)` }));
      return [...fornecedores, ...vendedores].sort((a, b) => a.nome.localeCompare(b.nome));
    }, [fornecedoresList, vendedoresList]);
    
    // Gera lista formatada para "Receber de" (Clientes)
    const beneficiariosReceber = useMemo(() => {
      return clientesList.map(c => ({ id: c.id, nome: c.nome })).sort((a, b) => a.nome.localeCompare(b.nome));
    }, [clientesList]);

    // Combina todas as categorias para filtros
    const todasCategorias = useMemo(() => {
      const set = new Set([...CATEGORIA_PAGAMENTO, ...CATEGORIA_RECEBIMENTO]);
      return Array.from(set).sort();
    }, []);

    return {
        contasList,
        clientesList,
        fornecedoresList,
        vendedoresList,
        beneficiariosPagar,
        beneficiariosReceber,
        todasCategorias,
        loadingDropdowns
    };
};